import {SubscriptionPriceModel} from "../../models/subscriptionPrice";
import React, {useEffect, useState} from "react";
import {HeroBody, HeroHeading1, HeroHeading2, TextStyles} from "../text";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {Button} from "../buttons/Button";
import Skeleton from "react-loading-skeleton";
import {createCheckoutSession, selectIsCreatingCheckout} from "../../redux/upgrade/subscriptionSlice";

export interface UpgradeSubscriptionCardViewProps {
  subscription: SubscriptionPriceModel
  availableSubscriptions: SubscriptionPriceModel[]
  isLoading: boolean
  isSelected: boolean
}

export const UpgradeSubscriptionCardView: React.FC<UpgradeSubscriptionCardViewProps> = ({ subscription,isSelected, availableSubscriptions, isLoading }) => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  const isCreatingSession = useAppSelector(state => selectIsCreatingCheckout(state, subscription.id))

  const formatter = new Intl.NumberFormat('en',{ style: 'currency', currency: subscription.currency })
  const savingsFormatter = new Intl.NumberFormat('en',{ style: 'currency', currency: subscription.currency, maximumFractionDigits: 0 })
  const periodPrice = formatter.format(subscription.price)
  const discountedPeriodPrice = subscription.discountPrice ? formatter.format(subscription.discountPrice) : undefined
  const monthlyPrice = formatter.format(subscription.monthlyPrice)
  const discountedMonthlyPrice: string | undefined = subscription.discountMonthlyPrice ? formatter.format(subscription.discountMonthlyPrice) : undefined

  const [period, setPeriod] = useState<string>()

  const mostExpensiveSubscription = availableSubscriptions.length
    ? availableSubscriptions.reduce((prev, curr) => prev.monthlyPrice > curr.monthlyPrice ? prev : curr)
    : undefined;

  const cheapestSubscription = availableSubscriptions.length
    ? availableSubscriptions.reduce((prev, curr) => prev.monthlyPrice < curr.monthlyPrice ? prev : curr)
    : undefined;

  const isBestOffer = cheapestSubscription?.id === subscription.id

  let yearlySavings: number | undefined;
  if (isBestOffer && mostExpensiveSubscription?.discountMonthlyPrice && subscription.discountMonthlyPrice) {
    yearlySavings = (mostExpensiveSubscription.discountMonthlyPrice * 12) - (subscription.discountMonthlyPrice * 12)
  } else if (isBestOffer && mostExpensiveSubscription) {
    yearlySavings = (mostExpensiveSubscription.monthlyPrice * 12) - (subscription.monthlyPrice * 12)
  }

  useEffect(() => {
    switch (subscription.interval) {
      case "day":
        setPeriod(t('subscription:subscription_title_daily'))
        break
      case "week":
        setPeriod(t('subscription:subscription_title_weekly'))
        break
      case "month":
        if (subscription.intervalCount === 3) {
          setPeriod(t('subscription:subscription_title_quarterly'))
        } else if (subscription.intervalCount === 6) {
          setPeriod(t('subscription:subscription_title_biannually'))
        } else {
          setPeriod(t('subscription:subscription_title_monthly'))
        }
        break
      case "year":
        setPeriod(t('subscription:subscription_title_yearly'))
        break
    }
  }, [subscription])

  return <>
    <div className="w-full text-left rounded-xl border-2 border-fg-gray-surface p-4 lg:p-6 hidden md:flex flex-col space-y-2">
      <HeroHeading2 className="font-bold">{isLoading ? <Skeleton className="!w-20" /> : period}</HeroHeading2>

      <HeroBody>{isLoading ? <Skeleton className="!w-42" /> : t('subscription:subscription_subtitle_billing', { period, price: discountedPeriodPrice ?? periodPrice })}</HeroBody>

      <div className="h-4" />

      <HeroHeading1 className="font-bold" >
      <span className="relative">
        {isLoading ? <Skeleton className="!w-24" /> : monthlyPrice}
        <div className={`${discountedMonthlyPrice ? '' : 'hidden'} absolute left-0 top-1/2 right-0 border-t-6 border-fg-primary transform -rotate-12`} />
      </span>
      </HeroHeading1>

      <HeroHeading2 className={`font-bold ${discountedMonthlyPrice ? '' : 'hidden'}`}>
        {isLoading ? <Skeleton className="!w-20" /> : discountedMonthlyPrice}
      </HeroHeading2>

      <HeroBody className="!font-semibold">{isLoading ? <Skeleton className="!w-28" /> : t('subscription:subscription_subtitle_per_period', { period: t('subscription:subscription_title_month').toLowerCase() })}</HeroBody>

      <div className="h-4" />

      <HeroBody className={`!font-semibold text-fg-primary ${isBestOffer ? '' : 'invisible'}`}>{isLoading ? <Skeleton className="!w-36" /> : t('subscription:subscription_subtitle_save_yearly', { amount: savingsFormatter.format(yearlySavings ?? 0) })}</HeroBody>

      <Button
        size="xl"
        isSkeletonLoading={isLoading}
        skeletonClassName="!rounded-xl"
        className="!rounded-xl !font-bold"
        isFullWidth
        isLoading={isCreatingSession}
        onClick={() => {
          dispatch(createCheckoutSession({ id: subscription.id }))
        }}
        buttonStyle={`${isBestOffer ? 'primary' : 'secondary'}`}
      >{t('subscription:subscription_title_select_plan')}</Button>
    </div>

    <div className={`w-full rounded-xl border-2 p-4 lg:p-6 flex space-x-2 md:hidden flex-grow ${isSelected && !isLoading ? 'border-fg-primary text-fg-primary' : 'border-fg-gray-surface'}`}>
      <div className="flex flex-col flex-grow text-left space-y-2">
        <HeroHeading2 className="font-bold">{isLoading ? <Skeleton className="!w-20" /> : period}</HeroHeading2>

        <div className={`flex-grow ${isBestOffer ? 'hidden' : ''}`} />

        <HeroBody>{isLoading ? <Skeleton className="!w-42" /> : t('subscription:subscription_subtitle_billing', { period, price: discountedPeriodPrice ?? periodPrice })}</HeroBody>

        <div className={`flex-grow ${isBestOffer ? '' : 'hidden'}`} />

        <HeroBody className={`!font-semibold text-fg-primary ${isBestOffer ? '' : 'hidden'}`}>{isLoading ? <Skeleton className="!w-36" /> : t('subscription:subscription_subtitle_save_yearly', { amount: savingsFormatter.format(yearlySavings ?? 0) })}</HeroBody>
      </div>

      <div className="flex flex-col space-y-2 flex-shrink-0">
        <HeroHeading1 className="font-bold text-right" >
          <span className="relative">
            {isLoading
              ? <Skeleton className="!w-24" />
              : <span>
                {monthlyPrice}
                <span className={`${discountedMonthlyPrice || isLoading ? 'hidden' : ''} ${TextStyles.HeroBody} lowercase`}>/{t('subscription:subscription_title_month')}</span>
              </span>
            }
            <div className={`${discountedMonthlyPrice ? '' : 'hidden'} absolute left-0 top-1/2 right-0 border-t-6 border-fg-primary transform -rotate-12`} />
          </span>
        </HeroHeading1>

        <HeroHeading2 className={`text-right font-bold ${discountedMonthlyPrice ? '' : 'hidden'}`}>
          {isLoading
            ? <Skeleton className="!w-20" />
            : <span>
                {discountedMonthlyPrice}
              <span className={`${isLoading ? 'hidden' : ''} ${TextStyles.HeroBody} lowercase`}>/{t('subscription:subscription_title_month')}</span>
              </span>
          }
        </HeroHeading2>
      </div>
    </div>
  </>
}